/**
 * packages/dados/src/sessao.ts — quem está logado e o que a interface pode mostrar.
 *
 * ENTRADA SÓ POR CONVITE (decisão de 26/07/2026). Ninguém se cadastra sozinho: o
 * gestor emite o convite, o banco guarda só o `sha256(token)` e o gatilho
 * `t_barrar_signup` recusa qualquer `signUp` cujo e-mail não tenha convite pendente.
 * O vínculo com a incorporadora não nasce no cadastro — nasce em
 * `app.aceitar_convite(token)`, que confere o token E o e-mail do JWT.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ ⚠ OS HELPERS DE PAPEL DESTE ARQUIVO NÃO SÃO SEGURANÇA.                   │
 * │                                                                          │
 * │ Este código roda no navegador: o usuário pode alterá-lo. Quem autoriza   │
 * │ de verdade é o BANCO — RLS, políticas por tabela e grants de coluna      │
 * │ (`db/migrations/0007_rls.sql`). Se `podeEditar` devolver `true` para     │
 * │ quem não pode, o banco recusa a escrita do mesmo jeito.                  │
 * │                                                                          │
 * │ O que estes helpers resolvem é OUTRO problema: não oferecer na tela um   │
 * │ botão que o banco vai negar. São um espelho das funções                  │
 * │ `app.emp_leitura()` / `app.emp_escrita()` / `app.emp_admin()` — quando   │
 * │ uma delas mudar no SQL, mude aqui junto, e a regra fica em UM lugar só   │
 * │ de cada lado. Última mudança: a 0031 (12/10/2026) pôs o Admin em         │
 * │ `emp_escrita`/`emp_admin` para toda incorporadora ativa — e o            │
 * │ tests/sessao.test.ts tem um caso por linha do SQL.                       │
 * └──────────────────────────────────────────────────────────────────────────┘
 */

import type { AuthChangeEvent, Session } from '@supabase/supabase-js';
import type { Database } from './banco.tipos';
import { ErroFincast, obterCliente } from './cliente';

/* ============================================================================
 * Tipos
 * ========================================================================== */

/**
 * Papel DENTRO de uma incorporadora. `admin_hype` não está aqui de propósito:
 * é papel de PLATAFORMA (`app.usuario.is_admin_hype`), não de empresa — mesma
 * separação do enum `app.papel` no banco.
 */
export type Papel = Database['app']['Enums']['papel'];

/** Vínculo do usuário com uma incorporadora. */
export interface VinculoEmpresa {
  empresaId: string;
  nome: string;
  /**
   * `null` = enxerga esta empresa por ser Admin, sem vínculo nela. Desde a 0031
   * (12/10/2026) o Admin lê, escreve e administra toda incorporadora ATIVA — com ou sem
   * vínculo: `emp_escrita()`/`emp_admin()` passaram a incluí-lo (o D2-1, "a plataforma
   * lê e não escreve", foi revogado pelo usuário em 11–12/10/2026).
   */
  papel: Papel | null;
  /** Empresa ativa. Empresa inativa some de `emp_leitura()` — nada é permitido nela. */
  ativa: boolean;
}

/** O usuário logado, do jeito que a interface precisa dele. */
export interface Perfil {
  id: string;
  nome: string;
  email: string;
  /**
   * Papel de plataforma (na tela, "Admin Fincast"; o nome interno ficou): enxerga todas as
   * incorporadoras e, desde a 0031, escreve e administra todas as ATIVAS.
   */
  isAdminHype: boolean;
  /** Usuário desativado mantém o login, mas o banco não devolve nada a ele. */
  ativo: boolean;
  /** Ordenada por nome. Para o Admin Hype, todas as empresas ativas. */
  empresas: VinculoEmpresa[];
}

/**
 * Situação da sessão. `sem_perfil` é real e precisa de tela própria: o usuário
 * autenticou mas ainda não tem linha em `app.usuario` — falta aceitar o convite.
 */
export type EstadoSessao =
  | { situacao: 'anonimo' }
  | { situacao: 'sem_perfil'; usuarioId: string; email: string }
  | { situacao: 'autenticado'; perfil: Perfil };

/* ============================================================================
 * Regras de papel (espelho do SQL — ver aviso no topo)
 * ========================================================================== */

function vinculo(perfil: Perfil, empresaId: string): VinculoEmpresa | undefined {
  return perfil.empresas.find((e) => e.empresaId === empresaId);
}

/*
 * `ativaAgora` (opcional): a situação da incorporadora lida AGORA do banco — o
 * `Empresa.ativa` de `listarEmpresas()` —, quando a tela já a tem em mãos. O perfil fica
 * em memória até trocar de usuário, e antes da 0031 isso era inofensivo (o Admin nunca
 * tinha botão). Agora três casos mentiriam sem ela: incorporadora criada por OUTRO admin
 * depois do login (não está no perfil: botões escondidos que o banco aceitaria),
 * arquivada nesta sessão (botões que o banco recusa) e restaurada nesta sessão. A fila de
 * pedidos chama `invalidarPerfil()` depois de decidir ou desfazer; as telas que já trazem a
 * lista fresca passam a situação por aqui.
 */

/**
 * Espelha `app.emp_escrita()` (0031): incorporadora ATIVA e (Admin, ou papel gestor ou
 * analista). O Admin — "Admin Fincast" na tela — edita em todas as ativas, com ou sem
 * vínculo (decisão de 11–12/10/2026, que revogou o D2-1).
 */
export function podeEditar(perfil: Perfil, empresaId: string, ativaAgora?: boolean): boolean {
  if (!perfil.ativo) return false;
  const v = vinculo(perfil, empresaId);
  if ((ativaAgora ?? v?.ativa) !== true) return false;
  return perfil.isAdminHype || v?.papel === 'gestor' || v?.papel === 'analista';
}

/**
 * Espelha `app.emp_admin()` (0031): incorporadora ATIVA e (Admin, ou papel gestor).
 * Convidar, trocar papel e desativar membro, arquivar cenário, autorizar extensão de
 * demanda. O Admin entra aqui também — mas continua sem mexer no PRÓPRIO vínculo
 * (`usuario_id <> auth.uid()` no banco) e sem contar como gestor no `chk_gestor`.
 */
export function podeAdministrar(perfil: Perfil, empresaId: string, ativaAgora?: boolean): boolean {
  if (!perfil.ativo) return false;
  const v = vinculo(perfil, empresaId);
  if ((ativaAgora ?? v?.ativa) !== true) return false;
  return perfil.isAdminHype || v?.papel === 'gestor';
}

/**
 * Entra sem ser membro — só o Admin. Desde a 0031 não quer mais dizer "só leitura": quer
 * dizer "de fora da equipe", o que mantém o aviso na tela e o registro de acesso
 * (`registrarAcessoSeForDeFora`).
 *
 * ADM-2 (revisão de 12/10/2026): a incorporadora que NÃO está no perfil (criada por outro admin
 * depois do login) também é "de fora" — `undefined === null` dava falso, e o Admin entrava sem o
 * aviso e sem o registro. Se ele virou membro dela depois do login, a função do banco reconhece
 * e não registra (0017: membro é acesso comum).
 */
export function acessoDePlataforma(perfil: Perfil, empresaId: string): boolean {
  return perfil.isAdminHype && (vinculo(perfil, empresaId)?.papel ?? null) === null;
}

/* ============================================================================
 * Tradução de erro
 * ========================================================================== */

/** Códigos que as RPCs e os gatilhos levantam, em texto que serve para a tela. */
const MENSAGENS: ReadonlyArray<readonly [string, string]> = [
  ['Invalid login credentials', 'E-mail ou senha incorretos.'],
  ['Email not confirmed', 'Confirme o e-mail que enviamos antes de entrar.'],
  ['Email rate limit exceeded', 'Muitas tentativas seguidas. Espere alguns minutos.'],
  ['over_email_send_rate_limit', 'Muitas tentativas seguidas. Espere alguns minutos.'],
  /* ORG-4b: o "esqueci a senha" não existe na tela (ver entrada.ts); quem orienta é o gestor. */
  ['User already registered', 'Já existe conta com este e-mail. Entre com a sua senha; se não lembrar, fale com o gestor da incorporadora.'],
  ['CADASTRO_SEM_CONVITE', 'Este e-mail não tem convite válido. Peça um convite ao gestor da incorporadora.'],
  ['CONVITE_INVALIDO', 'Convite inválido, já usado, revogado ou expirado. Peça um novo ao gestor.'],
  ['CONVITE_DE_OUTRO_EMAIL', 'Este convite foi emitido para outro e-mail. Entre com o e-mail que recebeu o convite.'],
  ['CONVITE_SEM_EMISSOR', 'Quem emitiu este convite não tem mais acesso à incorporadora. Peça um convite novo ao gestor.'],
  /* 0031 (D21): aceitar convite que a própria pessoa emitiu. Mesma frase do repositorios.ts. */
  ['CONVITE_PARA_SI', 'Ninguém pode convidar o próprio e-mail. Peça a outro gestor ou administrador que envie o convite.'],
  ['NAO_AUTENTICADO', 'Faça login antes de aceitar o convite.'],
  ['SEM_EMAIL_NO_TOKEN', 'Sua sessão não traz o e-mail. Saia e entre de novo.'],
];

function traduzir(bruto: string): { codigo: string; mensagem: string } {
  for (const [chave, texto] of MENSAGENS) {
    if (bruto.includes(chave)) return { codigo: chave, mensagem: texto };
  }
  return { codigo: 'ERRO_SUPABASE', mensagem: bruto };
}

function falhar(operacao: string, erro: { message: string } | null, causa?: unknown): never {
  const bruto = erro?.message ?? 'Falha desconhecida';
  const { codigo, mensagem } = traduzir(bruto);
  throw new ErroFincast(codigo, mensagem, { operacao, erro, causa });
}

/* ============================================================================
 * Autenticação
 * ========================================================================== */

/** Login com e-mail e senha. Devolve o perfil já carregado. */
export async function entrar(email: string, senha: string): Promise<Perfil> {
  const { data, error } = await obterCliente().auth.signInWithPassword({
    email: email.trim(),
    password: senha,
  });
  if (error) falhar('entrar', error);
  perfilEmCache = null;
  const perfil = await obterPerfil({ recarregar: true });
  if (perfil === null) {
    // autenticou, mas não há linha em app.usuario: convite ainda não aceito
    throw new ErroFincast(
      'SEM_PERFIL',
      'Sua conta existe mas ainda não está vinculada a nenhuma incorporadora. Aceite o convite pelo link do e-mail.',
      data.user?.id,
    );
  }
  return perfil;
}

/** Encerra a sessão neste navegador e limpa o perfil em memória. */
export async function sair(): Promise<void> {
  perfilEmCache = null;
  const { error } = await obterCliente().auth.signOut();
  if (error) falhar('sair', error);
}

/* Recuperar e redefinir senha: NÃO há funções aqui, de propósito (ORG-4, 12/10/2026). O
   "Esqueci minha senha" saiu porque sem servidor próprio de e-mail ele não funciona — ver o
   comentário do ESQUECI MINHA SENHA em apps/web/src/telas/entrada.ts. Quando houver SMTP,
   as duas chamadas do Supabase (`resetPasswordForEmail` e `updateUser({ password })`) voltam
   junto com a tela que as usa. */

/**
 * Avisa quando QUEM ESTÁ LOGADO muda — entrou, saiu, trocou de conta.
 * Devolve a função que cancela a assinatura.
 *
 * ⚠ NÃO avisa a cada evento do Supabase, e essa é a diferença que importa.
 * O cliente dispara evento de autenticação também por MANUTENÇÃO: renova o token, e
 * o faz sempre que a aba volta ao foco. Como quem escuta redesenha a tela — apaga o
 * miolo, escreve "Carregando" e remonta —, cada volta de aba produzia um piscar preto
 * e o app inteiro nascendo de novo, para chegar exatamente ao mesmo lugar.
 *
 * O critério passa a ser a IDENTIDADE: id de usuário diferente do último avisado. Sair
 * também passa, porque o id vira `null`. Renovação de token com a mesma pessoa não é
 * notícia — é o cliente fazendo o trabalho dele.
 *
 * O retorno de chamada é adiado um tique de propósito: chamar o Supabase de dentro
 * do `onAuthStateChange` trava o cliente (ele segura o mutex do auth enquanto o
 * ouvinte roda). Adiando, o ouvinte pode usar `obterPerfil()` à vontade.
 */
export function aoMudarAutenticacao(
  ouvinte: (evento: AuthChangeEvent, sessao: Session | null) => void,
): () => void {
  let ultimoAvisado: string | null | undefined = undefined;   // `undefined` = nunca avisei
  const { data } = obterCliente().auth.onAuthStateChange(
    (evento: AuthChangeEvent, sessao: Session | null) => {
      const quem = sessao?.user?.id ?? null;
      /* Mudou o cadastro da MESMA pessoa (nome, e-mail, senha): o perfil em memória
         envelheceu, mas a tela não precisa nascer de novo. */
      if (evento === 'USER_UPDATED') perfilEmCache = null;
      if (quem === ultimoAvisado) return;
      ultimoAvisado = quem;
      perfilEmCache = null; // mudou de pessoa: o perfil em memória não vale mais
      setTimeout(() => ouvinte(evento, sessao), 0);
    },
  );
  return () => data.subscription.unsubscribe();
}

/* ============================================================================
 * Convite
 * ========================================================================== */

/**
 * Aceita o convite: chama `app.aceitar_convite`, que confere `sha256(token)` E o
 * e-mail do JWT, cria a linha em `app.usuario` e o vínculo em `app.membro_empresa`.
 * Exige sessão ativa. Devolve o `empresa_id` em que o usuário entrou.
 */
export async function aceitarConvite(token: string): Promise<string> {
  const { data, error } = await obterCliente().rpc('aceitar_convite', { p_token: token.trim() });
  if (error) falhar('aceitarConvite', error);
  perfilEmCache = null; // ganhou vínculo novo
  return data;
}

export interface CadastroComConvite {
  email: string;
  senha: string;
  /** Token que veio no link do e-mail de convite. */
  token: string;
  nome?: string;
}

/** Resultado do cadastro. `precisaConfirmarEmail` muda a tela seguinte. */
export interface ResultadoCadastro {
  /**
   * `true` quando o projeto exige confirmação de e-mail: ainda não há sessão, então
   * o convite NÃO pôde ser aceito. Guarde o token e chame `aceitarConvite` depois
   * que o usuário voltar pelo link de confirmação.
   */
  precisaConfirmarEmail: boolean;
  /** Empresa em que entrou, quando o convite já foi aceito. */
  empresaId: string | null;
}

/**
 * Cadastro pelo convite: cria a conta e, se já houver sessão, aceita o convite na
 * sequência. O gatilho `t_barrar_signup` recusa o cadastro se não houver convite
 * pendente para este e-mail — é o que garante "ninguém se cadastra sozinho".
 */
export async function cadastrarComConvite(dados: CadastroComConvite): Promise<ResultadoCadastro> {
  const { data, error } = await obterCliente().auth.signUp({
    email: dados.email.trim(),
    password: dados.senha,
    options: { data: { nome: dados.nome ?? '' } }, // lido por t_barrar_signup → app.usuario.nome
  });
  if (error) falhar('cadastrarComConvite', error);
  if (data.session === null) return { precisaConfirmarEmail: true, empresaId: null };
  return { precisaConfirmarEmail: false, empresaId: await aceitarConvite(dados.token) };
}

/* ============================================================================
 * Perfil
 * ========================================================================== */

let perfilEmCache: Perfil | null = null;

/**
 * As três linhas que o perfil lê, ancoradas nos tipos gerados do banco: renomear uma
 * coluna no Postgres e regerar `banco.tipos.ts` quebra AQUI, no lugar certo, em vez
 * de virar `undefined` na tela.
 */
type Tabelas = Database['app']['Tables'];
type LinhaUsuario = Pick<Tabelas['usuario']['Row'], 'id' | 'nome' | 'email' | 'is_admin_hype' | 'ativo'>;
type LinhaMembro = Pick<Tabelas['membro_empresa']['Row'], 'empresa_id' | 'papel'>;
type LinhaEmpresa = Pick<Tabelas['empresa']['Row'], 'id' | 'nome' | 'ativa'>;

/**
 * Estado completo da sessão, em uma chamada só. Três consultas em paralelo, todas
 * filtradas pelo próprio banco:
 *   · `app.usuario`        → identidade e `is_admin_hype`
 *   · `app.membro_empresa` → meus vínculos ativos
 *   · `app.empresa`        → RLS já devolve exatamente `emp_leitura()`, ou seja,
 *                            minhas empresas — ou TODAS as ativas, se Admin Hype.
 *
 * A lista final vem do cruzamento: o que o banco deixou ver, com o papel que eu
 * tenho em cada uma. Não há montagem de permissão no cliente; só leitura do que a
 * política já resolveu.
 */
export async function obterEstadoSessao(opcoes?: { recarregar?: boolean }): Promise<EstadoSessao> {
  const cliente = obterCliente();
  const { data: dadosSessao, error: erroSessao } = await cliente.auth.getSession();
  if (erroSessao) falhar('obterEstadoSessao', erroSessao);
  const sessao = dadosSessao.session;
  if (sessao === null) {
    perfilEmCache = null;
    return { situacao: 'anonimo' };
  }
  const uid = sessao.user.id;

  if (opcoes?.recarregar !== true && perfilEmCache !== null && perfilEmCache.id === uid) {
    return { situacao: 'autenticado', perfil: perfilEmCache };
  }

  const [usuario, membros, empresas] = await Promise.all([
    cliente.from('usuario').select('id, nome, email, is_admin_hype, ativo').eq('id', uid).maybeSingle(),
    cliente.from('membro_empresa').select('empresa_id, papel').eq('usuario_id', uid).eq('ativo', true),
    cliente.from('empresa').select('id, nome, ativa'),
  ]);
  if (usuario.error) falhar('obterEstadoSessao/usuario', usuario.error);
  if (membros.error) falhar('obterEstadoSessao/membros', membros.error);
  if (empresas.error) falhar('obterEstadoSessao/empresas', empresas.error);

  const u: LinhaUsuario | null = usuario.data;
  if (u === null) {
    perfilEmCache = null;
    return { situacao: 'sem_perfil', usuarioId: uid, email: sessao.user.email ?? '' };
  }

  const papelPor = new Map<string, Papel>();
  const linhasMembro: LinhaMembro[] = membros.data ?? [];
  for (const m of linhasMembro) papelPor.set(m.empresa_id, m.papel);

  const linhasEmpresa: LinhaEmpresa[] = empresas.data ?? [];
  const vinculos: VinculoEmpresa[] = linhasEmpresa.map((e) => ({
    empresaId: e.id,
    nome: e.nome,
    papel: papelPor.get(e.id) ?? null,
    ativa: e.ativa,
  }));
  // vínculo cuja empresa o banco não devolveu = empresa inativa (some de emp_leitura).
  // Entra na lista com ativa=false para a tela explicar o sumiço em vez de simplesmente omitir.
  const vistas = new Set(vinculos.map((v) => v.empresaId));
  for (const [empresaId, papel] of papelPor) {
    if (!vistas.has(empresaId)) vinculos.push({ empresaId, nome: '', papel, ativa: false });
  }
  vinculos.sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));

  perfilEmCache = {
    id: u.id,
    nome: u.nome,
    email: u.email,
    isAdminHype: u.is_admin_hype,
    ativo: u.ativo,
    empresas: vinculos,
  };
  return { situacao: 'autenticado', perfil: perfilEmCache };
}

/** Atalho: o perfil, ou `null` se não há sessão ou o convite ainda não foi aceito. */
export async function obterPerfil(opcoes?: { recarregar?: boolean }): Promise<Perfil | null> {
  const estado = await obterEstadoSessao(opcoes);
  return estado.situacao === 'autenticado' ? estado.perfil : null;
}

/** Descarta o perfil em memória (após aceitar convite, trocar papel, etc.). */
export function invalidarPerfil(): void {
  perfilEmCache = null;
}

/**
 * Deixa rastro de que o Admin Hype abriu os dados de uma incorporadora da qual não
 * é membro (`app.registrar_acesso_admin`, migration 0017). Chame ao ABRIR a empresa.
 *
 * Registro DECLARATIVO, não controle: não existe gatilho de SELECT no Postgres. O
 * valor é de governança — poder mostrar ao cliente quando a Hype olhou os dados
 * dele. A própria função no banco ignora chamadas de quem é membro e não repete o
 * registro dentro da mesma hora, então pode ser chamada sem medo a cada navegação.
 * Falha aqui não interrompe a navegação.
 */
export async function registrarAcessoAdmin(empresaId: string, motivo?: string): Promise<void> {
  const { error } = await obterCliente().rpc('registrar_acesso_admin', {
    p_empresa: empresaId,
    p_motivo: motivo,
  });
  if (error) falhar('registrarAcessoAdmin', error);
}
